import { api } from './config';

const walletApi = api();

// ── Account ────────────────────────────────────────────────────────────────────
export const getMyAccount = async () => {
  try {
    const r = await walletApi.get('/wallet/account/my');
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const getAccountBalance = async () => {
  try {
    const r = await walletApi.get('/wallet/account/balance');
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Top-up Requests ────────────────────────────────────────────────────────────
export const submitTopupRequest = async (formData) => {
  try {
    const r = await walletApi.post('/wallet/topup/request', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const getMyTopupRequests = async (page = 1, limit = 10) => {
  try {
    const r = await walletApi.get('/wallet/topup/my-requests', { params: { page, limit } });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const cancelTopupRequest = async (requestId) => {
  try {
    const r = await walletApi.patch(`/wallet/topup/request/${requestId}/cancel`);
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Transaction History ────────────────────────────────────────────────────────
export const getMyTransactionHistory = async (page, pageSize, sort, search, filters = {}) => {
  try {
    const params = { page, pageSize, sort: JSON.stringify(sort), search };
    if (filters.currency) params.currency = filters.currency;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.minAmount) params.minAmount = filters.minAmount;
    if (filters.maxAmount) params.maxAmount = filters.maxAmount;

    const r = await walletApi.get('/wallet/transactions/history', { params });
    if (r.status === 200 && r.data.success) {
      return { status: 200, data: r.data.data, total: r.data.total, pagination: r.data.pagination };
    }
    return { status: 400, message: r.data.message || 'Failed to get transactions' };
  } catch (e) {
    if (e.response?.status === 400) {
      return { status: 400, message: e.response.data?.message || 'Failed to get transactions' };
    }
    throw e.response?.data || { message: e.message };
  }
};

export const getTransactionDetail = async (transactionId) => {
  try {
    const r = await walletApi.get(`/wallet/transactions/${transactionId}`);
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Currency Exchange ─────────────────────────────────────────────────────────
export const exchangeCurrency = async (fromCurrency, toCurrency, amount) => {
  try {
    const r = await walletApi.post('/wallet/exchange', { fromCurrency, toCurrency, amount });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Admin: Top-up management ──────────────────────────────────────────────────
export const getPendingTopupRequests = async (page = 1, limit = 20) => {
  try {
    const r = await walletApi.get('/wallet/admin/topup/pending', { params: { page, limit } });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const getAllTopupRequests = async (page = 1, limit = 20, status = 'all', search = '', dateFrom = '', dateTo = '', customerId = '') => {
  try {
    const r = await walletApi.get('/wallet/admin/topup/all', { params: { page, limit, status, search, dateFrom, dateTo, customerId } });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const getTopupRequestDetails = async (requestId) => {
  try {
    const r = await walletApi.get(`/wallet/admin/topup/${requestId}`);
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const approveTopupRequest = async (requestId, approvalNotes) => {
  try {
    const r = await walletApi.post(`/wallet/admin/topup/${requestId}/approve`, { approvalNotes });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const rejectTopupRequest = async (requestId, rejectionReason) => {
  try {
    const r = await walletApi.post(`/wallet/admin/topup/${requestId}/reject`, { rejectionReason });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Admin: Balance adjustment ─────────────────────────────────────────────────
export const adminAdjustBalance = async (accountId, amount, currency, adjustType, reason) => {
  try {
    const r = await walletApi.post(`/wallet/admin/account/${accountId}/adjust`, {
      amount, currency, adjustType, reason,
    });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Admin: Convert currency inside a customer's wallet ────────────────────────
export const adminExchangeCurrency = async (accountId, fromCurrency, toCurrency, amount, reason) => {
  try {
    const r = await walletApi.post(`/wallet/admin/account/${accountId}/exchange`, {
      fromCurrency, toCurrency, amount, reason,
    });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Admin: Account management ─────────────────────────────────────────────────
export const getAllAccounts = async (page = 1, limit = 20, search = '', status = 'all') => {
  try {
    const r = await walletApi.get('/wallet/admin/accounts', { params: { page, limit, search, status } });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// Fetch a single customer's wallet account by customerId (staff side). Returns the
// raw response; on error returns a safe { success:false } so callers can degrade.
export const getCustomerAccountByAdmin = async (customerId) => {
  try {
    const r = await walletApi.get(`/wallet/admin/account/${customerId}`);
    return r.data;
  } catch (e) {
    return e.response?.data || { success: false, message: e.message };
  }
};

export const getAccountTransactions = async (accountId, page = 1, limit = 20, currency, txn_type, purpose, dateFrom, dateTo, search) => {
  try {
    const params = { page, limit };
    if (currency) params.currency = currency;
    if (txn_type) params.txn_type = txn_type;
    if (purpose) params.purpose = purpose;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (search) params.search = search;
    const r = await walletApi.get(`/wallet/admin/account/${accountId}/transactions`, { params });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const adminGetTransactionDetail = async (txnId) => {
  try {
    const r = await walletApi.get(`/wallet/admin/transactions/${txnId}`);
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const toggleAccountStatus = async (accountId) => {
  try {
    const r = await walletApi.patch(`/wallet/admin/account/${accountId}/toggle-status`);
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Admin: Cash payment & purchase order ─────────────────────────────────────
export const adminCreateCashPayment = async (formData) => {
  try {
    const r = await walletApi.post('/wallet/admin/cash-payment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const adminRecordPurchase = async (accountId, formData) => {
  try {
    const r = await walletApi.post(`/wallet/admin/account/${accountId}/purchase`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

export const adminRecordRefund = async (accountId, formData) => {
  try {
    const r = await walletApi.post(`/wallet/admin/account/${accountId}/refund`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Admin: Stats ─────────────────────────────────────────────────────────────
export const getWalletStats = async (dateFrom, dateTo) => {
  try {
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const r = await walletApi.get('/wallet/admin/stats', { params });
    return r.data;
  } catch (e) {
    throw e.response?.data || { message: e.message };
  }
};

// ── Utility ───────────────────────────────────────────────────────────────────
export const getEvidencePhotoUrl = (filename) => {
  const base = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
  return `${base}/wallet/evidence/${filename}`;
};

export default {
  getMyAccount, getAccountBalance,
  submitTopupRequest, getMyTopupRequests, cancelTopupRequest,
  getMyTransactionHistory, exchangeCurrency,
  getPendingTopupRequests, getAllTopupRequests, getTopupRequestDetails,
  approveTopupRequest, rejectTopupRequest,
  adminAdjustBalance, adminExchangeCurrency, getWalletStats,
  getCustomerAccountByAdmin,
  getEvidencePhotoUrl,
};
